import React from 'react'
// @src imports
import { useQueries, useSelector, useState, useVertical } from 'src/hooks'
import {
  ComparisonIncludesList,
  UpsaleDialog,
} from '@sendoutcards/quantum-design-ui'
import { UpsaleOptionType } from '@sendoutcards/quantum-design-ui/dist/src/portfolios/soc/upsaleDialog/upsaleOptions'
import { Portal } from 'src/portal/portal'
import { getAffiliateUpsaleDialogMarketingContent } from 'src/legacy_graphql'

interface AffiliateUpsaleDialogProps {
  isEligibleForFreeGsAffiliateAccount: boolean
  hasFullAccessFreeAffiliate: boolean
  onClose: () => void
}

const comparisonFreeOption = [
  {
    title: 'Earn 20% Commissions',
    description:
      'Receives an average of 20% commissions on everything you personally sell.  As an affiliate you can offer all of the Promptings product lines.',
    isIncluded: true,
    maxWidth: '455px',
  },
  {
    title: 'Affiliate Plan with Network Marketing Opportunity',
    description:
      'Build a team of like mind affiliates with the ability to build residual income through team and leadership bonuses.',
    isIncluded: true,
    maxWidth: '455px',
  },
  {
    title: 'Sales Pages',
    description: 'Personalized funnel sales pages written and done for you.',
    isIncluded: true,
    maxWidth: '455px',
  },
  {
    title: 'Mobile App Business Building Tool',
    description:
      'Your personalized tool for social sharing and keeping track of your connections and prospects.  Tracks and notifies you when your connections visit your sales pages.',
    isIncluded: false,
    maxWidth: '455px',
  },
  {
    title: 'Online Business Office',
    description:
      'Manage your business including details on what your customers and affiliates purchase along with your team’s activity.',
    isIncluded: false,
    maxWidth: '455px',
  },
  {
    title: 'Weekly Newsletter & Shareable Content',
    description:
      'Pre-written newsletters and shareable content that you can send to your connections in one simple click, to stay top of mind and drive people to your sales pages.',
    isIncluded: false,
    maxWidth: '455px',
  },
]

const comparisonProOption = [
  {
    title: 'Earn 20% Commissions',
    description:
      'Receives an average of 20% commissions on everything you personally sell.  As an affiliate you can offer all of the Promptings product lines.',
    isIncluded: true,
    maxWidth: '455px',
  },
  {
    title: 'Affiliate Plan with Network Marketing Opportunity',
    description:
      'Build a team of like mind affiliates with the ability to build residual income through team and leadership bonuses.',
    isIncluded: true,
    maxWidth: '455px',
  },
  {
    title: 'Sales Pages',
    description: 'Personalized funnel sales pages written and done for you.',
    isIncluded: true,
    maxWidth: '455px',
  },
  {
    title: 'Mobile App Business Building Tool',
    description:
      'Your personalized tool for social sharing and keeping track of your connections and prospects.  Tracks and notifies you when your connections visit your sales pages.',
    isIncluded: true,
    maxWidth: '455px',
  },
  {
    title: 'Online Business Office',
    description:
      'Manage your business including details on what your customers and affiliates purchase along with your team’s activity.',
    isIncluded: true,
    maxWidth: '455px',
  },
  {
    title: 'Weekly Newsletter & Shareable Content',
    description:
      'Pre-written newsletters and shareable content that you can send to your connections in one simple click, to stay top of mind and drive people to your sales pages.',
    isIncluded: true,
    maxWidth: '455px',
  },
]

const AffiliateUpsaleDialog: React.FC<AffiliateUpsaleDialogProps> = props => {
  const {
    isEligibleForFreeGsAffiliateAccount,
    hasFullAccessFreeAffiliate,
    onClose,
  } = props

  const [marketingContent] = useQueries(
    getAffiliateUpsaleDialogMarketingContent(),
  )

  const allOptions: (UpsaleOptionType & { secondaryTitle?: string })[] = [
    {
      title: hasFullAccessFreeAffiliate
        ? marketingContent.affiliateProTitleFreeAffiliate.content
        : marketingContent.affiliateProTitle.content,
      secondaryTitle:
        (hasFullAccessFreeAffiliate
          ? marketingContent.affiliateProSecondaryTitleFreeAffiliate.content
          : marketingContent.affiliateProSecondaryTitle.content) ?? null,
      subtitle: hasFullAccessFreeAffiliate
        ? marketingContent.affiliateProSubtitleFreeAffiliate.content
        : marketingContent.affiliateProSubtitle.content,
      optionId: 'pro',
    },
    {
      title: 'Free Affiliate',
      subtitle: 'Try it without the tools',
      optionId: 'free',
    },
  ]

  const [options, setOptions] = useState(
    allOptions.filter(option => option.optionId === 'pro'),
  )
  const [selectedOption, setSelectedOption] = useState('')

  const { width } = useSelector(s => s.window)
  const vertical = useVertical()

  const getPlan = (optionId: string) => {
    return allOptions.find(option => option.optionId === optionId)
  }

  const handleSelectedOption = (optionId: string) => {
    setSelectedOption(getPlan(optionId)?.optionId ?? 'pro')
  }

  const handleAcceptSelectedAction = () => {
    return {
      title: 'Update Subscription',
      onClick: () => {
        window.open(
          `/?client=${
            vertical && vertical.id !== 'soc' ? vertical.id : 'promptings'
          }&affiliatejoin&package=${selectedOption}`,
          '_self',
          'noopener,noreferrer',
        )
      },
    }
  }

  const getExtraLink = () => {
    // Temporary disabling free option
    const shouldBlockFreeOption = true
    if (isEligibleForFreeGsAffiliateAccount && !shouldBlockFreeOption) {
      return {
        title: 'View more options',
        onClick: () => {
          setOptions(allOptions)
        },
      }
    } else {
      return undefined
    }
  }

  return (
    <Portal dataMarketingContent="affiliateProTitle | affiliateProSecondaryTitle | affiliateProSubtitle">
      <UpsaleDialog
        title="Great Choice!"
        description="We are glad you found the plan that works best for you. Please confirm below."
        insetOverride="x0"
        maxWidth="calc(100% - 150px)"
        maxHeight="calc(100% - 150px)"
        optionFormLocation="right"
        setIsDialogOpen={onClose}
        isOpen={true}
        selectableOptions={options}
        selectedOption={selectedOption}
        handleSelectedOption={handleSelectedOption}
        removeSelectedOption={() => setSelectedOption('')}
        upsaleContentTitle="Subscription Includes"
        checkoutTitle="Great Choice!"
        checkoutDescription="We are glad you found the plan that works best for you. Please confirm below."
        zIndex={3000}
        upsaleContent={
          <ComparisonIncludesList
            isMinified={width <= 1017}
            items={
              selectedOption === 'free'
                ? comparisonFreeOption
                : comparisonProOption
            }
          />
        }
        acceptSelectedAction={handleAcceptSelectedAction()}
        extraLink={getExtraLink()}
        shouldHideSendFreeCard={true}
      >
        <div />
      </UpsaleDialog>
    </Portal>
  )
}

export default AffiliateUpsaleDialog
